import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Card,
    CardContent,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    CircularProgress,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip,
    IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DescriptionIcon from '@mui/icons-material/Description';
import EmailIcon from '@mui/icons-material/Email';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNotify } from 'react-admin';
import { DocumentTemplate } from './types';
import { resolveAssignedTemplate } from './templateResolver';
import { buildTemplatesUrl } from './templatesApi';
import { DefaultPreviewDialog } from './DefaultPreviewDialog';

const baseApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_URL = baseApiUrl.endsWith('/api') ? baseApiUrl : `${baseApiUrl}/api`;

interface TemplatePurpose {
    slug: string;
    name: string;
    description: string;
    required: boolean;
    type: string;
    assignedTemplate: {
        id: number;
        name: string;
        isCustom: boolean;
        currentVersionNumber: number;
    } | null;
    systemTemplate: {
        id: number;
        name: string;
        isCustom: boolean;
        currentVersionNumber: number;
    } | null;
    hasTemplate: boolean;
    usingSystemDefault: boolean;
    sentWithoutTemplate: boolean;
}

interface PurposeGroup {
    type: string;
    typeLabel: string;
    purposes: TemplatePurpose[];
}

interface TemplateAssignmentsProps {
    onEditTemplate: (template: DocumentTemplate) => void;
}

const typeIcons: Record<string, React.ReactElement> = {
    'invoice': <DescriptionIcon />,
    'email': <EmailIcon />,
    'certificate': <CardGiftcardIcon />,
};

const typeColors: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'info'> = {
    'invoice': 'primary',
    'email': 'info',
    'certificate': 'success',
};

export const TemplateAssignments: React.FC<TemplateAssignmentsProps> = ({ onEditTemplate }) => {
    const notify = useNotify();
    const [purposeGroups, setPurposeGroups] = useState<PurposeGroup[]>([]);
    const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Assign dialog
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [selectedPurpose, setSelectedPurpose] = useState<TemplatePurpose | null>(null);
    const [assigning, setAssigning] = useState(false);

    // Unassign dialog
    const [unassignDialogOpen, setUnassignDialogOpen] = useState(false);
    const [unassigning, setUnassigning] = useState(false);

    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);

    const getToken = () => localStorage.getItem('token');

    const loadPurposes = useCallback(async () => {
        const token = getToken();
        if (!token) return;

        try {
            const response = await fetch(`${API_URL}/document-templates/purposes`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Fehler beim Laden der Verwendungszwecke');
            }

            const data = await response.json();
            setPurposeGroups(data);
        } catch (err) {
            console.error('Error loading purposes:', err);
            setError('Fehler beim Laden der Verwendungszwecke');
        }
    }, []);

    const loadTemplates = useCallback(async () => {
        const token = getToken();
        if (!token) return;

        try {
            const response = await fetch(buildTemplatesUrl(API_URL), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/ld+json',
                },
            });

            if (!response.ok) {
                throw new Error('Fehler beim Laden der Vorlagen');
            }

            const data = await response.json();
            const templates = data['hydra:member'] || data['member'] || [];
            setTemplates(templates);
        } catch (err) {
            console.error('Error loading templates:', err);
        }
    }, []);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            await Promise.all([loadPurposes(), loadTemplates()]);
            setLoading(false);
        };
        load();
    }, [loadPurposes, loadTemplates]);

    const handleAssign = async (templateId: number) => {
        if (!selectedPurpose) return;
        const token = getToken();
        if (!token) return;

        setAssigning(true);
        try {
            const response = await fetch(`${API_URL}/document-templates/purposes/${selectedPurpose.slug}/assign`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({ templateId }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Fehler bei der Zuweisung');
            }

            notify('Vorlage erfolgreich zugewiesen', { type: 'success' });
            setAssignDialogOpen(false);
            loadPurposes();
        } catch (err: any) {
            console.error('Error assigning template:', err);
            notify(err.message || 'Fehler bei der Zuweisung', { type: 'error' });
        } finally {
            setAssigning(false);
        }
    };

    const handleCreateForPurpose = async () => {
        if (!selectedPurpose) return;
        const token = getToken();
        if (!token) return;

        setAssigning(true);
        try {
            const response = await fetch(`${API_URL}/document-templates/purposes/${selectedPurpose.slug}/create`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({}),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Fehler beim Erstellen');
            }

            const result = await response.json();
            notify('Vorlage erfolgreich erstellt', { type: 'success' });
            setAssignDialogOpen(false);
            loadPurposes();

            // Open editor for the new template
            if (result.template) {
                onEditTemplate(result.template);
            }
        } catch (err: any) {
            console.error('Error creating template:', err);
            notify(err.message || 'Fehler beim Erstellen', { type: 'error' });
        } finally {
            setAssigning(false);
        }
    };

    const handleUnassign = async () => {
        if (!selectedPurpose) return;
        const token = getToken();
        if (!token) return;

        setUnassigning(true);
        try {
            const response = await fetch(`${API_URL}/document-templates/purposes/${selectedPurpose.slug}/unassign`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json',
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Fehler beim Entfernen');
            }

            notify('Eigene Vorlage entfernt', { type: 'success' });
            setUnassignDialogOpen(false);
            loadPurposes();
        } catch (err: any) {
            console.error('Error unassigning template:', err);
            notify(err.message || 'Fehler beim Entfernen', { type: 'error' });
        } finally {
            setUnassigning(false);
        }
    };

    const openAssignDialog = (purpose: TemplatePurpose) => {
        setSelectedPurpose(purpose);
        setAssignDialogOpen(true);
    };

    const openUnassignDialog = (purpose: TemplatePurpose) => {
        setSelectedPurpose(purpose);
        setUnassignDialogOpen(true);
    };

    const openPreviewDialog = (purpose: TemplatePurpose) => {
        setSelectedPurpose(purpose);
        setPreviewDialogOpen(true);
    };

    const openAssignedTemplate = async (id: number) => {
        const token = getToken();
        try {
            const template = await resolveAssignedTemplate(id, templates, async (tid) => {
                const response = await fetch(`${API_URL}/document_templates/${tid}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept': 'application/ld+json',
                    },
                });
                if (!response.ok) throw new Error('Vorlage nicht gefunden');
                return response.json();
            });
            onEditTemplate(template);
        } catch (err) {
            console.error('Error loading assigned template:', err);
            notify('Vorlage konnte nicht geladen werden', { type: 'error' });
        }
    };

    const getAvailableTemplates = () => {
        if (!selectedPurpose) return [];
        return templates.filter(t => t.type === selectedPurpose.type);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                {error}
            </Alert>
        );
    }

    return (
        <Box>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Weisen Sie Ihren Dokumentvorlagen bestimmte Verwendungszwecke zu.
                Ohne eigene Vorlage verschickt das System seinen Standardtext.
                Über „Standard ansehen“ sehen Sie, wie er aussieht.
            </Typography>

            {purposeGroups.map((group) => (
                <Accordion key={group.type} defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {typeIcons[group.type]}
                            <Typography variant="h6">{group.typeLabel}</Typography>
                            <Chip
                                label={`${group.purposes.filter(p => p.assignedTemplate).length}/${group.purposes.length} zugewiesen`}
                                size="small"
                                color={group.purposes.every(p => p.hasTemplate) ? 'success' : 'default'}
                                variant="outlined"
                            />
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {group.purposes.map((purpose) => (
                                <Card key={purpose.slug} variant="outlined">
                                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, '&:last-child': { pb: 2 } }}>
                                        {/* Status Icon */}
                                        <Box sx={{ minWidth: 40 }}>
                                            {purpose.assignedTemplate ? (
                                                <Tooltip title="Eigene Vorlage zugewiesen">
                                                    <CheckCircleIcon color="success" />
                                                </Tooltip>
                                            ) : purpose.systemTemplate ? (
                                                <Tooltip title="System-Standard wird verwendet">
                                                    <CheckCircleIcon color="info" />
                                                </Tooltip>
                                            ) : purpose.sentWithoutTemplate ? (
                                                <Tooltip title="Das System verschickt seinen Standardtext">
                                                    <CheckCircleIcon color="info" />
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title="Ohne eigene Vorlage wird diese E-Mail nicht verschickt">
                                                    <InfoOutlinedIcon color="info" />
                                                </Tooltip>
                                            )}
                                        </Box>

                                        {/* Purpose Info */}
                                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                            <Typography variant="subtitle1" fontWeight="bold">
                                                {purpose.name}
                                                {purpose.required && (
                                                    <Chip label="Erforderlich" size="small" color="error" variant="outlined" sx={{ ml: 1 }} />
                                                )}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {purpose.description}
                                            </Typography>
                                        </Box>

                                        {/* Current Assignment */}
                                        <Box sx={{ minWidth: 200, textAlign: 'right', flexShrink: 0 }}>
                                            {purpose.assignedTemplate ? (
                                                <Box>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {purpose.assignedTemplate.name}
                                                    </Typography>
                                                    <Chip label="Eigene Vorlage" size="small" color="success" />
                                                </Box>
                                            ) : purpose.systemTemplate ? (
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {purpose.systemTemplate.name}
                                                    </Typography>
                                                    <Chip label="System-Standard" size="small" color="info" variant="outlined" />
                                                </Box>
                                            ) : purpose.sentWithoutTemplate ? (
                                                <Chip label="Standardtext" size="small" color="default" variant="outlined" />
                                            ) : (
                                                <Typography variant="body2" color="text.primary">
                                                    Nur mit eigener Vorlage
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* Actions */}
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexShrink: 0, minWidth: { xs: 0, md: 280 }, whiteSpace: 'nowrap' }}>
                                            {purpose.sentWithoutTemplate && (
                                                <Button
                                                    variant="text"
                                                    size="small"
                                                    startIcon={<VisibilityIcon />}
                                                    onClick={() => openPreviewDialog(purpose)}
                                                >
                                                    Standard ansehen
                                                </Button>
                                            )}
                                            {purpose.assignedTemplate ? (
                                                <>
                                                    <Tooltip title="Vorlage bearbeiten">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                if (purpose.assignedTemplate) openAssignedTemplate(purpose.assignedTemplate.id);
                                                            }}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Andere Vorlage zuweisen">
                                                        <IconButton size="small" onClick={() => openAssignDialog(purpose)}>
                                                            <SwapHorizIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Eigene Vorlage entfernen">
                                                        <IconButton size="small" color="error" onClick={() => openUnassignDialog(purpose)}>
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<AddIcon />}
                                                    onClick={() => openAssignDialog(purpose)}
                                                >
                                                    Zuweisen
                                                </Button>
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            ))}

            {/* Assign Dialog */}
            <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Vorlage zuweisen: {selectedPurpose?.name}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Wählen Sie eine bestehende Vorlage oder erstellen Sie eine neue.
                    </Typography>

                    <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<AddIcon />}
                        onClick={handleCreateForPurpose}
                        disabled={assigning}
                        sx={{ mb: 2 }}
                    >
                        Neue Vorlage erstellen
                    </Button>

                    {getAvailableTemplates().length > 0 && (
                        <>
                            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                Oder bestehende Vorlage verwenden:
                            </Typography>
                            <List sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                {getAvailableTemplates().map((template) => (
                                    <ListItem key={template.id} disablePadding>
                                        <ListItemButton
                                            onClick={() => handleAssign(template.id)}
                                            disabled={assigning}
                                        >
                                            <ListItemIcon>
                                                {typeIcons[template.type]}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={template.name}
                                                secondary={template.description || `v${template.currentVersionNumber}`}
                                            />
                                            {template.companyId === null && (
                                                <Chip label="System" size="small" variant="outlined" />
                                            )}
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        </>
                    )}

                    {assigning && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignDialogOpen(false)}>Abbrechen</Button>
                </DialogActions>
            </Dialog>

            {/* Unassign Dialog */}
            <Dialog open={unassignDialogOpen} onClose={() => setUnassignDialogOpen(false)}>
                <DialogTitle>Eigene Vorlage entfernen?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Möchten Sie die eigene Vorlage für "{selectedPurpose?.name}" entfernen?
                    </Typography>
                    {selectedPurpose?.systemTemplate ? (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            Es wird dann die System-Standardvorlage "{selectedPurpose.systemTemplate.name}" verwendet.
                        </Alert>
                    ) : selectedPurpose?.sentWithoutTemplate ? (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            Danach verschickt das System wieder seinen Standardtext.
                        </Alert>
                    ) : (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            Danach wird diese E-Mail nicht mehr verschickt.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUnassignDialogOpen(false)}>Abbrechen</Button>
                    <Button
                        onClick={handleUnassign}
                        color="error"
                        variant="contained"
                        disabled={unassigning}
                    >
                        {unassigning ? <CircularProgress size={24} /> : 'Entfernen'}
                    </Button>
                </DialogActions>
            </Dialog>

            <DefaultPreviewDialog
                open={previewDialogOpen}
                purpose={selectedPurpose}
                onClose={() => setPreviewDialogOpen(false)}
            />
        </Box>
    );
};

export default TemplateAssignments;
